import {FC, useEffect, useMemo, useState} from 'react'
import {api, MaintenanceRequest, RequestStatusValue, RequestTypeValue, ApiError} from '../../services/api'
import {Pagination} from '../../components/Pagination'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {RequestChat} from '../../components/RequestChat'
import {PageHead} from '../../components/PageHead'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'
import {useIsMobile} from '../../hooks/useIsMobile'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'
const PAGE_SIZE = 10

const TABS: {label: string; value: RequestStatusValue | 'ALL'}[] = [
  {label: 'Open', value: 'OPEN'},
  {label: 'In progress', value: 'IN_PROGRESS'},
  {label: 'Resolved', value: 'RESOLVED'},
  {label: 'Closed', value: 'CLOSED'},
  {label: 'All', value: 'ALL'},
]

const STATUS_TONE: Record<RequestStatusValue, {color: string; bg: string; label: string}> = {
  OPEN: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)', label: 'Open'},
  IN_PROGRESS: {color: '#2b4eff', bg: 'rgba(43,78,255,0.12)', label: 'In progress'},
  RESOLVED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)', label: 'Resolved'},
  CLOSED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)', label: 'Closed'},
}
const TYPE_LABEL: Record<RequestTypeValue, string> = {CLEANING: '🧹 Cleaning', MAINTENANCE: '🔧 Maintenance', SUGGESTION: '💡 Suggestion'}
const STATUSES: RequestStatusValue[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']
const TYPE_OPTIONS: {label: string; value: string}[] = [
  {label: 'All types', value: ''},
  {label: 'Cleaning', value: 'CLEANING'},
  {label: 'Maintenance', value: 'MAINTENANCE'},
  {label: 'Suggestion', value: 'SUGGESTION'},
]

const Pill: FC<{status: RequestStatusValue}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{t.label}</span>
}

const RequestsPage: FC = () => {
  const toast = useToast()
  const [tab, setTab] = useState<RequestStatusValue | 'ALL'>('OPEN')
  const [rows, setRows] = useState<MaintenanceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [manage, setManage] = useState<MaintenanceRequest | null>(null)
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await api.listRequests(tab === 'ALL' ? undefined : tab))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (typeFilter && r.type !== typeFilter) return false
      if (!q) return true
      const name = `${r.renter?.firstName ?? ''} ${r.renter?.lastName ?? ''}`
      return `${r.title} ${r.description} ${name}`.toLowerCase().includes(q)
    })
  }, [rows, search, typeFilter])

  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page])

  useEffect(() => {
    setPage(1)
  }, [search, typeFilter])

  const onUpdated = (updated: MaintenanceRequest) => {
    if (tab !== 'ALL' && updated.status !== tab) {
      setRows((list) => list.filter((x) => x.id !== updated.id))
    } else {
      setRows((list) => list.map((x) => (x.id === updated.id ? updated : x)))
    }
    setManage(null)
    toast.success(`Request marked ${STATUS_TONE[updated.status].label.toLowerCase()}.`)
  }

  return (
    <div>
      <PageHead icon='🧹' eyebrow='Operations' title='Cleaning & Maintenance' sub='Triage renter requests and move them through to resolution.' />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search requests…'>
        <FilterSelect value={typeFilter} onChange={setTypeFilter} options={TYPE_OPTIONS} ariaLabel='Filter by type' />
      </ListToolbar>

      <div style={{display: 'flex', gap: isMobile ? 6 : 8, marginBottom: 18, flexWrap: 'wrap'}}>
        {TABS.map((t) => (
          <button key={t.value} onClick={() => setTab(t.value)} style={{fontSize: isMobile ? 13 : 14, fontWeight: 600, padding: isMobile ? '7px 12px' : '8px 16px', borderRadius: 999, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', border: '1px solid', borderColor: tab === t.value ? ACCENT : '#e5e7eb', background: tab === t.value ? ACCENT : '#fff', color: tab === t.value ? '#fff' : INK}}>
            {t.label}
          </button>
        ))}
      </div>

      <div className='card ccp-fade-up-2' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        <div className='card-body p-0'>
          {loading ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
          ) : error ? (
            <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>
              {search.trim() || typeFilter ? 'No requests match your search.' : `No ${tab === 'ALL' ? '' : STATUS_TONE[tab as RequestStatusValue].label.toLowerCase()} requests.`}
            </div>
          ) : (
            <>
              {isMobile ? (
                <div>
                  {paged.map((r) => (
                    <div key={r.id} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                        <div style={{minWidth: 0}}>
                          <div style={{fontWeight: 600, color: INK}}>{r.renter?.firstName} {r.renter?.lastName}</div>
                          <div style={{color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{r.renter?.email}</div>
                        </div>
                        <Pill status={r.status} />
                      </div>
                      <div style={{color: INK, marginTop: 8, fontSize: 14}}>{r.title}</div>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 4, fontSize: 13, color: MUTED}}>
                        <span>{TYPE_LABEL[r.type]}</span>
                        <span>Bed {r.bed?.code ?? '—'}</span>
                        <span>{new Date(r.createdAt).toLocaleDateString()}</span>
                      </div>
                      <button onClick={() => setManage(r)} style={{...btnPrimary, width: '100%', marginTop: 12}}>Manage</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                    <thead>
                      <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        <th style={th}>Renter</th>
                        <th style={th}>Bed</th>
                        <th style={th}>Type</th>
                        <th style={th}>Subject</th>
                        <th style={th}>Status</th>
                        <th style={th}>Date</th>
                        <th style={{...th, textAlign: 'right'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((r) => (
                        <tr key={r.id} style={{borderTop: '1px solid #f1f3f7'}}>
                          <td style={td}>
                            <div style={{fontWeight: 600, color: INK}}>{r.renter?.firstName} {r.renter?.lastName}</div>
                            <div style={{color: MUTED, fontSize: 13}}>{r.renter?.email}</div>
                          </td>
                          <td style={{...td, fontWeight: 700, color: INK}}>{r.bed?.code ?? '—'}</td>
                          <td style={{...td, color: MUTED, whiteSpace: 'nowrap'}}>{TYPE_LABEL[r.type]}</td>
                          <td style={{...td, color: INK, maxWidth: 240}}>{r.title}</td>
                          <td style={td}><Pill status={r.status} /></td>
                          <td style={{...td, color: MUTED}}>{new Date(r.createdAt).toLocaleDateString()}</td>
                          <td style={{...td, textAlign: 'right'}}>
                            <button onClick={() => setManage(r)} style={{...btnPrimary, minWidth: 80}}>Manage</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{borderTop: '1px solid #f1f3f7'}}>
                <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {manage && <ModalPortal><ManageModal request={manage} onClose={() => setManage(null)} onUpdated={onUpdated} /></ModalPortal>}
    </div>
  )
}

const ManageModal: FC<{request: MaintenanceRequest; onClose: () => void; onUpdated: (r: MaintenanceRequest) => void}> = ({request: r, onClose, onUpdated}) => {
  const [status, setStatus] = useState<RequestStatusValue>(r.status)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const viewAttachment = async () => {
    if (!r.attachmentKey) return
    try {
      const {url} = await api.fileBlobUrl(r.attachmentKey)
      window.open(url, '_blank', 'noopener')
    } catch {
      setErr('Could not open attachment')
    }
  }

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      onUpdated(await api.updateRequestStatus(r.id, status, note.trim() || undefined))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 28px', width: '100%', maxWidth: 920, minHeight: 'calc(100vh - 48px)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6}}>
          <h2 style={{fontSize: 19, fontWeight: 800, color: INK, margin: 0}}>{r.title}</h2>
          <Pill status={r.status} />
        </div>
        <div style={{fontSize: 13, color: MUTED, marginBottom: 14}}>{TYPE_LABEL[r.type]}{r.bed ? ` · Bed ${r.bed.code}` : ''} · {r.renter?.firstName} {r.renter?.lastName}</div>

        <p style={{fontSize: 14, color: '#475569', lineHeight: 1.5, margin: '0 0 14px'}}>{r.description}</p>
        {r.attachmentKey && (
          <button onClick={viewAttachment} style={{...btnGhost, marginBottom: 16}}>📎 View attachment</button>
        )}

        <div style={{borderTop: '1px solid #f1f3f7', paddingTop: 16, marginBottom: 18}}>
          <RequestChat requestId={r.id} mySide='ADMIN' />
        </div>

        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

        <div style={{marginBottom: 14}}>
          <label style={lbl}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as RequestStatusValue)} style={inp}>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_TONE[s].label}</option>)}
          </select>
        </div>
        <div style={{marginBottom: 4}}>
          <label style={lbl}>Note to renter (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder='e.g. Scheduled for tomorrow morning.' style={{...inp, minHeight: 70, resize: 'vertical'}} />
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={save} style={{...btnPrimary, minWidth: 120}} disabled={busy}>{busy ? <Spinner size={14} /> : 'Save & notify'}</button>
        </div>
      </div>
    </div>
  )
}

const th: React.CSSProperties = {padding: '14px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}
const lbl: React.CSSProperties = {display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}

export {RequestsPage}
